import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { mangas, categories } from '@/mocks/mangaData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function MangaDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const manga = mangas.find((m) => m.slug === slug);

  const relatedMangas = useMemo(() => {
    if (!manga) return [];
    return mangas
      .filter((m) => m.slug !== manga.slug && m.categories.some((c) => manga.categories.includes(c)))
      .slice(0, 5);
  }, [manga]);

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pt-24 pb-14 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">漫画不存在</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const mangaCategory = categories.find((c) => manga.categories.includes(c.slug));

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tuhaomanhua.com/' },
      ...(mangaCategory ? [{ '@type': 'ListItem' as const, position: 2, name: mangaCategory.name, item: `https://www.tuhaomanhua.com/category/${mangaCategory.slug}` }] : []),
      { '@type': 'ListItem', position: mangaCategory ? 3 : 2, name: manga.title, item: `https://www.tuhaomanhua.com/manga/${manga.slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 漫画全集下拉式免费阅读`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    datePublished: manga.chapters[0]?.updatedAt || manga.updatedAt,
    dateModified: manga.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '土豪漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.tuhaomanhua.com/manga/${manga.slug}`,
    },
  };

  return (
    <>
      <SchemaOrg data={breadcrumbSchema} />
      <SchemaOrg data={articleSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(mangaCategory ? [{ label: mangaCategory.name, href: `/category/${mangaCategory.slug}` }] : []),
              { label: manga.title },
            ]}
          />

          {/* Manga Info Section */}
          <article className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={manga.coverImage}
                  alt={`${manga.title} 漫画封面`}
                  title={`${manga.title} - 土豪漫画网免费阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                {manga.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {manga.author}
                </span>
                <span className="text-foreground-300">|</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  manga.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {manga.status}
                </span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i> {manga.rating} 分
                </span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {manga.views > 10000 ? `${(manga.views / 10000).toFixed(1)}万` : manga.views} 阅读
                </span>
                <span className="text-foreground-300">|</span>
                <span>共 {manga.chapters.length} 话</span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {manga.categories.map((catSlug) => {
                  const cat = categories.find((c) => c.slug === catSlug);
                  return cat ? (
                    <Link
                      key={catSlug}
                      to={`/category/${catSlug}`}
                      className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium hover:bg-accent-200 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ) : null;
                })}
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {manga.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-600 rounded text-xs whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-4">
                <h2 className="font-semibold text-foreground-900 text-sm mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading text-foreground-900">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 text-xs text-foreground-600 hover:text-foreground-900 bg-background-100 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                  正序 <i className="ri-arrow-down-s-line"></i>
                </button>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200/70">
                {manga.chapters.map((chapter) => (
                  <a
                    key={chapter.id}
                    href={`/manga/${manga.slug}/chapter/${chapter.number}`}
                    className="flex items-center justify-between px-4 py-3 bg-background-50 hover:bg-background-100 transition-colors group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updatedAt}</span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* Related Manga */}
          {relatedMangas.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading text-foreground-900">相关推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {relatedMangas.map((rm) => (
                  <MangaCard key={rm.slug} manga={rm} />
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}